import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled/macro';
import { DateTime } from 'luxon';
import TooltipPortal from '../common/TooltipPortal';
import { fetchCommentsForObservation } from '../../store/api';
import { receiveCommentsForFunction } from '../../store/comments/commentsActions';
import {
  getCommentsForFunction,
  getCommentCountForFunction,
} from '../../store/comments/commentsSelectors';

const Popover = styled.div`
  position: fixed;
  min-width: 220px;
  max-width: 320px;
  padding: 8px 10px;
  background-color: var(--color-tooltip-bg);
  color: var(--color-white);
  border-radius: 8px;
  font-size: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  z-index: 1200;
  pointer-events: none;
`;

const Header = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  margin-bottom: 4px;
  font-size: 0.7rem;
  color: var(--color-gray);
`;

const Author = styled.span`
  font-weight: bold;
  color: ${(props) => (props.role === 'automation' ? 'var(--color-green)' : 'var(--color-white)')};
`;

const Body = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.3;
`;

const Footer = styled.div`
  margin-top: 6px;
  font-size: 0.65rem;
  color: var(--color-charcoal-light);
  text-transform: uppercase;
`;

const formatTime = (iso) => {
  const dt = DateTime.fromISO(iso);
  const diff = DateTime.utc().diff(dt, ['minutes', 'hours', 'days']).toObject();
  if (diff.days >= 1) return dt.toFormat('LLL d, HH:mm');
  if (diff.hours >= 1) return `${Math.floor(diff.hours)}h ago`;
  if (diff.minutes >= 1) return `${Math.floor(diff.minutes)}m ago`;
  return 'just now';
};

const CommentHoverPopover = ({ functionId, anchorRect }) => {
  const dispatch = useDispatch();
  const comments = useSelector(getCommentsForFunction(functionId));
  const count = useSelector(getCommentCountForFunction(functionId));
  const fetched = useRef(false);
  // Mirror of comments.length, readable at response time: hovering the button is
  // how the full thread gets opened, so this preview fetch (5) can race the
  // thread's fetch (50) — if ours lands last it must not clobber the loaded thread.
  const hasComments = useRef(comments.length > 0);
  hasComments.current = comments.length > 0;

  useEffect(() => {
    if (!anchorRect || count === 0) return;
    if (fetched.current || comments.length > 0) return;
    fetched.current = true;
    fetchCommentsForObservation(functionId, { limit: 5 })
      .then(({ response }) => {
        if (!response || hasComments.current) return;
        dispatch(receiveCommentsForFunction({ functionId, comments: response.comments || [] }));
      })
      .catch(() => {});
  }, [anchorRect, count, comments.length, functionId, dispatch]);

  if (!anchorRect || count === 0) return null;
  const latest = comments[0];
  if (!latest) return null;

  const top = Math.min(anchorRect.bottom + 8, window.innerHeight - 120);
  const left = Math.min(anchorRect.left, window.innerWidth - 340);

  return (
    <TooltipPortal>
      <Popover style={{ top, left }}>
        <Header>
          <Author role={latest.authorRole}>{latest.author}</Author>
          <span>{formatTime(latest.createdAt)}</span>
        </Header>
        {latest.text ? (
          <Body>{latest.text}</Body>
        ) : (
          <Body style={{ fontStyle: 'italic', color: 'var(--color-gray)' }}>
            ({(latest.type || 'note').replace('_', ' ')})
          </Body>
        )}
        {count > 1 && <Footer>{count} comments total</Footer>}
      </Popover>
    </TooltipPortal>
  );
};

export default CommentHoverPopover;
